import React, { useMemo, useState } from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import Responsive from 'react-responsive';

import NavDropdown from 'scenes/Header/components/NavDropdown';
import NavDropdownSubmenu from 'scenes/Header/components/NavDropdown/components/NavDropdownSubmenu';
import UpgradeRecruiterAccount from 'scenes/Dialog/components/UpgradeRecruiterAccount';
import CreateProfileDialog from 'scenes/Dialog/components/CreateProfileDialog';
import CandidateProfileNotApproved from 'scenes/Dialog/components/CandidateProfileNotApproved';
import ConfirmYourProfile from 'scenes/Dialog/components/ConfirmYourProfile';
import AccessLimitsAlert from 'scenes/Dialog/components/AccessLimitsAlert';
import NoAddReviewRight from 'scenes/Dialog/components/NoAddReviewRight';
import BuyCreditsDialog from 'scenes/Dialog/components/BuyCreditsDialog';
import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import EditCandidatePositionForm from 'scenes/Form/components/EditCandidatePositionForm';

import { ROUTE } from 'constants/route';
import { POSITION_FLAG } from 'constants/profile';
import { Role, Option } from 'helpers/user';
import { getMainMenuSchema, ERRORS, CLICK_HANDLERS } from './schema';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

import './styles.css';

const MainNavigation = props => {
  const {
    user,
    counters,
    showMobileNav,
    history,
    location: { pathname },
    actions,
  } = props;
  const [toggledIndex, setToggledIndex] = useState(null);
  const { toggleMobileMenuHandler } = actions;

  const [
    isRecruiter,
    hasCandidateAccount,
    isCandidateEmailConfirm,
  ] = useMemo(
    () => [
      Role.isRecruiter(user),
      Option.hasCandidateAccount(user.options),
      Option.isCandidateEmailConfirm(user.options),
    ],
    [user]
  );

  const onClickAccessControl = (e, item) => {
    if (item.disabled) {
      e.preventDefault();
      if (item.disabled.reason === ERRORS.UPGRADE_RECRUITER_ACCOUNT) {
        UpgradeRecruiterAccount();
      } else if (item.disabled.reason === ERRORS.BECOME_CANDIDATE) {
        if (!hasCandidateAccount) {
          CreateProfileDialog();
        } else if (hasCandidateAccount && !isCandidateEmailConfirm) {
          ConfirmYourProfile();
        } else {
          CandidateProfileNotApproved();
        }
      } else if (item.disabled.reason === ERRORS.ACCESS_LIMITS) {
        AccessLimitsAlert();
      } else if (item.disabled.reason === ERRORS.NO_ADD_REVIEW_RIGHT) {
        NoAddReviewRight({ type: item.disabled.type });
      } else if (item.disabled.reason === ERRORS.PURCHASE_MORE_CREDITS) {
        BuyCreditsDialog({ history });
      } else if (item.disabled.reason === ERRORS.MANAGE_EMAILS_TEMPLATES) {
        BasicDialog({
          bodyRender:
            'You cannot access Manage Emails Templates if you are not allowed to access Manage Process and Manage Projects pages',
        });
      }
    } else if (item.onClickHandler) {
      e.preventDefault();
      if (item.onClickHandler === CLICK_HANDLERS.ADD_CANDIDATE_POSITION) {
        handleAddPosition();
      }
    }
    toggleMobileMenuHandler(false);
    setToggledIndex(null);
  };

  const toggleMobileNav = () => {
    showMobileNav
      ? toggleMobileMenuHandler(false)
      : toggleMobileMenuHandler(true);
  };

  const toggledIndexHandler = index => {
    setToggledIndex(index);
  };

  const handleAddPosition = () => {
    FormDialog({
      title: 'Add Position',
      modeless: false,
      compact: true,
      params: { isRecruiter },
      form: (actions, params) => (
        <EditCandidatePositionForm
          actions={actions}
          params={params}
          type={POSITION_FLAG.POSITION}
        />
      ),
    }).then(
      logout => {
        if (logout) {
          window.location = '/logout';
        } else if (pathname !== ROUTE.CANDIDATE_PROFILE) {
          props.history.push(ROUTE.CANDIDATE_PROFILE);
        } else {
          props.history.push('/');
          props.history.replace(ROUTE.CANDIDATE_PROFILE);
        }
      },
      response => ResponseErrorDialog({ response })
    );
  };

  const chevron = showMobileNav ? (
    <FontAwesomeIcon icon={faChevronUp} color="#fff" size="sm" />
  ) : (
    <FontAwesomeIcon icon={faChevronDown} color="#fff" size="sm" />
  );

  const Mobile = props => <Responsive {...props} maxWidth={767} />;
  const Desktop = props => <Responsive {...props} minWidth={768} />;

  const menu = useMemo(
    () =>
      getMainMenuSchema(user, pathname).map((item, index) => {
        const { visible, children, href, name, disabled, isActive } = item;
        return (
          visible && (
            <li className="main-navigation__item-list-item" key={index}>
              {children ? (
                <NavDropdown
                  item={item}
                  isActive={isActive}
                  navClass="main-navigation__link"
                  activeClassName="main-navigation__link--selected"
                  onClick={onClickAccessControl}
                  toggleIndex={index}
                  toggledIndex={toggledIndex}
                  toggleFunc={toggledIndexHandler}
                >
                  <NavDropdownSubmenu
                    items={children}
                    onClick={onClickAccessControl}
                  />
                </NavDropdown>
              ) : (
                <NavLink
                  exact
                  isActive={() => isActive}
                  to={disabled ? '#' : href}
                  className="main-navigation__link"
                  activeClassName="main-navigation__link--selected"
                  onClick={e => onClickAccessControl(e, item, {})}
                >
                  {name}
                  {counters[name.toLowerCase()] ? (
                    <span className="main-navigation__link-count-value">
                      ({counters[name.toLowerCase()]})
                    </span>
                  ) : (
                    ''
                  )}
                </NavLink>
              )}
            </li>
          )
        );
      }),
    [user, pathname, toggledIndex]
  );

  return (
    <nav className="main-navigation ">
      <Desktop>
        <ul className="main-navigation__item-list">{menu}</ul>
      </Desktop>
      <Mobile>
        <button
          className={
            showMobileNav
              ? 'main-navigation__mobile-toggler main-navigation__mobile-toggler--active ignore-react-onclickoutside'
              : 'main-navigation__mobile-toggler ignore-react-onclickoutside'
          }
          onClick={toggleMobileNav}
        >
          <span>Menu</span>
          <span className="main-navigation__mobile-toggler-icon">
            {chevron}
          </span>
        </button>
        {showMobileNav && (
          <div className="main-navigation__mobile-nav ignore-react-onclickoutside">
            <ul className="main-navigation__item-list main-navigation__item-list--mobile">
              {menu}
            </ul>
          </div>
        )}
      </Mobile>
    </nav>
  );
};

export default withRouter(MainNavigation);
